import { DocumentIcon } from '@heroicons/react/24/solid'
import dayjs from 'dayjs'
import { FC, useMemo } from 'react'

import { FinanceInvoice, InvoiceStatus } from '@/api/models/finance'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { cn } from '@/app/utils'
import { AppButton } from '@/components/shared/button'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { formatRuble } from '@/utils/number'

import { BalanceInvoiceProp } from '../BillsPayDrawer/BillsPayDrawer.types'
import {
  getBalanceInvoice,
  isInvoice
} from '../BillsPayDrawer/BillsPayDrawer.utils'
import { variantStyles } from './BillsListItem.constants'
import { BillsListItemVariant } from './BillsListItem.types'
import InvoiceButtonText from './InvoiceButtonText'

type Props = {
  invoice: BalanceInvoiceProp
  unpaid: boolean
  variant?: BillsListItemVariant
  className?: string
  onFileLinkClick?: (link: string) => void
  onPayClick?: (invoice: FinanceInvoice) => void
}

export const BillsListItem: FC<Props> = ({
  invoice,
  unpaid,
  variant = 'primary',
  className,
  onPayClick,
  onFileLinkClick
}) => {
  const { reachGoal } = useYandexMetrika()
  const balanceInvoice = useMemo(() => getBalanceInvoice(invoice), [invoice])
  const styles =
    variant === 'primary' ? variantStyles.primary : variantStyles.secondary

  const onFileButtonClick = (invoiceId: string) => {
    onFileLinkClick?.(invoiceId)
    reachGoal(YandexMetrikaEvent.INVOICES_VIEW_DOC)
  }

  return (
    <li
      className={cn(
        'flex items-center justify-between gap-20 rounded-16 p-24 md:p-14',
        'sm:grid sm:grid-cols-[auto_auto] sm:items-start sm:gap-12 sm:p-12',
        styles.root,
        className
      )}
      data-testid="bill-list-item"
    >
      <div className={cn('flex flex-col justify-between', styles.leftSide)}>
        <h3 className="font-bold text-20 md:text-16">{balanceInvoice.name}</h3>

        <div className="flex gap-8 text-grey-80 text-16 md:text-14">
          {isInvoice(invoice) ? (
            <>
              <p>{invoice.premiseName || invoice.customerName}</p>
              <span className="font-bold">•</span>
              <p className="capitalize">
                {dayjs(invoice.invoicePeriod)
                  .tz('Europe/Moscow')
                  .format('MMMM YYYY')}
              </p>
            </>
          ) : (
            <p>Общий счёт</p>
          )}
        </div>
      </div>

      <div
        className={cn('flex shrink-0 items-center gap-16', styles.rightSide)}
      >
        <p
          className={cn(
            'shrink-0 text-right font-bold !text-28 md:!text-24',
            unpaid && styles.debtSum
          )}
        >
          {formatRuble(
            unpaid ? balanceInvoice.debtSum : balanceInvoice.totalSum
          )}
        </p>

        <div
          className={cn(
            'flex items-center gap-8',
            styles.buttons,
            !onFileLinkClick && !onPayClick && 'hidden'
          )}
        >
          {onFileLinkClick && isInvoice(invoice) && (
            <AppButton
              variant="backless"
              size="small"
              className="!px-14"
              data-testid="bill-receipt-button-id"
              onClick={() => onFileButtonClick(invoice.id)}
            >
              <DocumentIcon className="h-16 w-16 text-grey-100" />
            </AppButton>
          )}
          {onPayClick && isInvoice(invoice) && (
            <AppButton
              variant={
                unpaid && invoice.status !== InvoiceStatus.isProcessing
                  ? 'secondary'
                  : 'backless'
              }
              size="small"
              disabled={
                !unpaid ||
                (unpaid && invoice.status === InvoiceStatus.isProcessing)
              }
              className="flex items-center gap-8 sm:grow"
              data-testid="bill-pay-button-id"
              onClick={() => onPayClick(invoice)}
            >
              <InvoiceButtonText unpaid={unpaid} status={invoice.status} />
            </AppButton>
          )}
        </div>
      </div>
    </li>
  )
}
